// Analyses (/jobs, DESIGN_SPEC §11.3; RESTYLE §5): every run, newest first, child runs under their parent.
//   compact head (title + "updated 2 s ago", one mono line generated from the loaded runs) · filter row kept in the URL ·
//   day-grouped table whose rows say what each run says, not only its record (§12.17) · "Load 50 more" + legend
//   families (family.ts FAMILY_RULE): a child sits under its parent in the parent's place; filters match runs one by
//   one; folded parents are remembered for the browser session
//   keys: j / k move the selected row, Enter opens it, ArrowRight / ArrowLeft unfold / fold, / searches, Esc clears the filters
//   <JobsPage />   route "analyses"
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Arrow, Button, Empty, Kbd, Loading, Time, Tooltip, VerdictGlyph, buttonClass } from "@/components/ui";
import { useShareLiveRuns } from "@/components/shell/useLiveRuns";
import { useClock, useHotkeys, useJobs, useNodes } from "@/hooks";
import { isApiError } from "@/lib/api";
import { ago } from "@/lib/format";
import { jobName } from "@/lib/records";
import { arrange, FAMILY_RULE } from "@/features/analyses/family";
import { FilterBar } from "@/features/analyses/FilterBar";
import { chipCounts, cleared, describe, isFiltered, matches, PAGE, readFilters, toParams, type Filters } from "@/features/analyses/filters";
import { JobsTable } from "@/features/analyses/JobsTable";
import { livenessOf, queuePositions, sortNewest } from "@/features/analyses/listState";
import { useEffectiveJobs } from "@/features/analyses/useEffectiveJobs";

/** The row selected last, so coming back from a report keeps the place. */
let lastSelected: string | null = null;

const FOLDED_KEY = "malbox-analyses-folded";

function readFolded(): Set<string> {
  try {
    const v: unknown = JSON.parse(sessionStorage.getItem(FOLDED_KEY) ?? "[]");
    return new Set(Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  } catch {
    return new Set();
  }
}

function storeFolded(ids: ReadonlySet<string>) {
  try {
    sessionStorage.setItem(FOLDED_KEY, JSON.stringify([...ids]));
  } catch {
    /* a per-session convenience only */
  }
}

function Updated({ at, failed }: { at: number; failed: boolean }) {
  const now = useClock(1000, at > 0);
  if (!at) return null;
  return (
    <>
      updated <Time value={at}>{ago(at, Math.max(now, at))}</Time>
      {failed ? " · refresh failed, retrying" : " · every 5 s"}
    </>
  );
}

function Legend() {
  const v = (k: "benign" | "suspicious" | "malicious") => (
    <span className={`v v--${k}`} aria-hidden="true">
      <VerdictGlyph v={k} />
    </span>
  );
  return (
    <p className="meta flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="inline-flex items-center gap-1.5">{v("benign")}benign 0–24</span>
      <span className="inline-flex items-center gap-1.5">{v("suspicious")}suspicious 25–59</span>
      <span className="inline-flex items-center gap-1.5">{v("malicious")}malicious 60–100</span>
      <span className="inline-flex items-center gap-1.5 max-[719px]:hidden">
        <span className="text-ink-ghost" aria-hidden="true">
          |
        </span>
        <Kbd keys={["j", "k"]} /> move · <Kbd>Enter</Kbd> open · <Kbd keys={["←", "→"]} /> fold · <Kbd>/</Kbd> search
      </span>
    </p>
  );
}

export function JobsPage() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);

  // The search box answers at once; the URL follows after a pause (and back/forward flows back in).
  const [q, setQ] = useState(filters.q);
  const written = useRef(filters.q);
  useEffect(() => {
    if (filters.q === written.current) return;
    written.current = filters.q;
    setQ(filters.q);
  }, [filters.q]);
  useEffect(() => {
    if (q === written.current) return;
    const t = setTimeout(() => {
      written.current = q;
      setParams(toParams({ ...filters, q }, params), { replace: true, preventScrollReset: true });
    }, 300);
    return () => clearTimeout(t);
  }, [q, filters, params, setParams]);

  const live = useMemo<Filters>(() => ({ ...filters, q }), [filters, q]);
  const update = useCallback(
    (next: Filters) => {
      written.current = next.q;
      setQ(next.q);
      setParams(toParams(next, params), { replace: true, preventScrollReset: true });
    },
    [params, setParams],
  );

  const jobsQ = useJobs(filters.limit);
  const jobs = useMemo(() => (jobsQ.data ? sortNewest(jobsQ.data) : undefined), [jobsQ.data]);
  const { rows, observe } = useEffectiveJobs(jobs);
  const { data: nodes } = useNodes();
  // the chip and the status bar flip a run to overdue within 10 s of its timeout (rows tick every second)
  const now = useClock(10_000);

  const [folded, setFolded] = useState(readFolded);
  const tree = useMemo(() => arrange(rows, (e) => matches(e, live, { now }), folded), [rows, live, folded, now]);
  const shown = tree.visible;
  const counts = useMemo(() => chipCounts(rows, live, now), [rows, live, now]);
  const split = useMemo(() => {
    if (!jobsQ.data) return null;
    let count = 0;
    let overdue = 0;
    for (const e of rows) {
      const l = livenessOf(e, now);
      if (l === "live") count++;
      else if (l === "overdue") overdue++;
    }
    return { count, overdue };
  }, [rows, now, jobsQ.data]);
  useShareLiveRuns(split);
  // more may exist when the page came back full, and while a larger page loads (the old rows are held)
  const capped = jobsQ.isPlaceholderData || (jobsQ.data?.length ?? 0) >= filters.limit;
  const queue = useMemo(() => queuePositions(rows), [rows]);
  const offered = useMemo(() => (nodes ? new Set(nodes.flatMap((n) => n.profiles ?? [])) : null), [nodes]);
  const names = useMemo(() => new Map(rows.map((e) => [e.id, jobName(e.job)])), [rows]);
  const profiles = useMemo(
    () => [...new Set([...rows.map((e) => e.job.profile), ...(nodes ?? []).flatMap((n) => n.profiles ?? []), filters.profile].filter(Boolean))].sort(),
    [rows, nodes, filters.profile],
  );

  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(lastSelected);
  const rowEls = useRef(new Map<string, HTMLTableRowElement>());
  const register = useCallback((id: string, el: HTMLTableRowElement | null) => {
    if (el) rowEls.current.set(id, el);
    else rowEls.current.delete(id);
  }, []);
  const select = useCallback((id: string) => {
    lastSelected = id;
    setSelected(id);
  }, []);
  const open = useCallback(
    (id: string) => {
      lastSelected = id;
      void navigate(`/jobs/${id}`, { viewTransition: true });
    },
    [navigate],
  );
  const focusRow = useCallback(
    (id: string) => {
      select(id);
      const el = rowEls.current.get(id);
      el?.focus({ preventScroll: true });
      el?.scrollIntoView({ block: "nearest" });
    },
    [select],
  );
  const move = (step: 1 | -1) => {
    if (!shown.length) return;
    const i = selected ? shown.findIndex((e) => e.id === selected) : -1;
    focusRow(shown[i < 0 ? 0 : Math.min(shown.length - 1, Math.max(0, i + step))].id);
  };
  const parentOf = useMemo(() => new Map(rows.map((e) => [e.id, e.job.parent_job_id])), [rows]);
  const fold = useCallback(
    (id: string, on: boolean) => {
      setFolded((prev) => {
        if (prev.has(id) === on) return prev;
        const next = new Set(prev);
        if (on) next.add(id);
        else next.delete(id);
        storeFolded(next);
        return next;
      });
      // folding away the selected row hands the selection to the folded parent
      if (!on || !selected || selected === id) return;
      const seen = new Set<string>();
      for (let p = parentOf.get(selected); p && !seen.has(p); p = parentOf.get(p)) {
        if (p === id) return focusRow(id);
        seen.add(p);
      }
    },
    [selected, parentOf, focusRow],
  );
  useHotkeys({
    j: () => move(1),
    k: () => move(-1),
    Escape: () => {
      if (!isFiltered(live)) return false;
      update(cleared(live));
    },
  });

  const stale = jobsQ.isPlaceholderData || (jobsQ.isError && !!jobsQ.data);
  const err = jobsQ.error;

  let body;
  if (!jobsQ.data) {
    body = jobsQ.isError ? (
      <Alert
        head="Couldn't load analyses"
        actions={
          <button type="button" className="lnk" onClick={() => void jobsQ.refetch()}>
            Retry now
          </button>
        }
      >
        {isApiError(err) ? `${err.status} from ${err.path || "/v1/jobs"}` : "the controller did not answer"} · retrying every 5 s
      </Alert>
    ) : (
      <Loading verb="Loading" className="border-t border-rule-strong py-4">
        analyses…
      </Loading>
    );
  } else if (rows.length === 0) {
    body = (
      <Empty
        className="border-t border-rule-cell"
        action={
          <div className="mt-2 grid justify-items-center gap-3">
            <Link className={buttonClass({ variant: "primary" })} to="/submit" viewTransition>
              Submit a sample <Arrow />
            </Link>
          </div>
        }
      >
        No analyses yet.
      </Empty>
    );
  } else if (tree.matched === 0) {
    const parts = describe(live);
    body = (
      <p className="filterline border-t border-rule-strong">
        <span>
          No runs match{" "}
          {parts.map((p, i) => (
            <span key={i}>
              {i > 0 && " + "}
              {p.quoted ? <q className="text-ink">{p.text}</q> : <b>{p.text}</b>}
            </span>
          ))}
          .
        </span>
        <span className="inline-flex items-center gap-2">
          <button type="button" className="lnk" onClick={() => update(cleared(live))}>
            Clear filters
          </button>
          <Kbd>Esc</Kbd>
        </span>
      </p>
    );
  } else {
    body = (
      <JobsTable
        groups={tree.groups}
        selected={selected}
        stale={stale}
        query={q}
        queue={queue}
        offered={offered}
        names={names}
        onSelect={select}
        onOpen={open}
        onFold={fold}
        onFocusRow={focusRow}
        register={register}
        observe={observe}
      />
    );
  }

  const matched = tree.matched;
  const filtered = matched !== rows.length;
  const families = rows.some((e) => !!e.job.parent_job_id);
  return (
    <div className="page">
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1.5 pt-7 pb-4">
        <h1 className="font-sans text-[28px] leading-[1.1] font-semibold tracking-[-.02em] text-ink">Analyses</h1>
        {jobsQ.dataUpdatedAt > 0 && (
          <p className="meta">
            <Updated at={jobsQ.dataUpdatedAt} failed={jobsQ.isError} />
          </p>
        )}
      </header>

      {/* nothing to filter in an empty ledger */}
      {(!jobsQ.data || rows.length > 0) && (
        <FilterBar q={q} onQuery={setQ} filters={live} counts={jobsQ.data ? counts : undefined} profiles={profiles} onChange={update} />
      )}

      {body}

      {rows.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-8 gap-y-3">
          <p className="meta flex flex-wrap items-center gap-x-3 gap-y-2">
            <span>
              {capped
                ? filtered
                  ? `${matched} of the ${rows.length} newest runs match`
                  : `Showing the ${rows.length} newest runs`
                : filtered
                  ? `Showing ${matched} of ${rows.length} runs`
                  : `Showing all ${rows.length} runs`}
              {families && (
                <>
                  {" · "}
                  <Tooltip lead="Child runs" text={FAMILY_RULE}>
                    <span className="cursor-help border-b border-dotted border-ink-faint" tabIndex={0}>
                      children under their parent
                    </span>
                  </Tooltip>
                </>
              )}
              {" · ran = dispatch to end · times UTC"}
            </span>
            {capped && (
              <Button variant="secondary" size="sm" disabled={jobsQ.isPlaceholderData} onClick={() => update({ ...live, limit: filters.limit + PAGE })}>
                {jobsQ.isPlaceholderData ? "Loading…" : `Load ${PAGE} more`}
              </Button>
            )}
          </p>
          <Legend />
        </div>
      )}
    </div>
  );
}
